import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { APP_INSIGHTS_INSTRUMENTATION_KEY } from '^configs/environments';
import IAppInsightsCustomProperties from '^types/insights/IAppInsightsCustomProperties';

class Logger {
  private appInsights: ApplicationInsights | null = null;

  private trackValue = (value: unknown, properties: IAppInsightsCustomProperties, severityLevel: SeverityLevel): void => {
    if (this.appInsights) {
      properties.route = window.location.href;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- app-insights v3 types `SeverityLevel` as `number | eSeverityLevel`; the `number` arm trips the rule, but the comparison is correct (callers always pass the enum member). No behavior change.
      if (severityLevel === SeverityLevel.Critical || severityLevel === SeverityLevel.Error) {
        this.appInsights.trackException({
          properties,
          severityLevel,
          error: value as Error, // callers (trackError/trackCriticalError) always pass an Error; cast is type-only.
        });

        return;
      }

      this.appInsights.trackTrace({
        properties,
        severityLevel,
        message: value as string, // callers (trackInfo/trackWarning) always pass a string; cast is type-only.
      });

      return;
    }

    /* eslint-disable @typescript-eslint/no-unsafe-enum-comparison -- SeverityLevel is `number | eSeverityLevel` in app-insights v3; the `number` arm trips the rule but the case labels are the correct enum members. No behavior change. */
    switch (severityLevel) {
      case SeverityLevel.Critical:
      case SeverityLevel.Error:
        console.error(value);
        break;

      case SeverityLevel.Warning:
        console.warn(value);
        break;

      default:
        console.info(value);
        break;
    }
    /* eslint-enable @typescript-eslint/no-unsafe-enum-comparison */
  };

  public init = (): void => {
    if (this.appInsights || !APP_INSIGHTS_INSTRUMENTATION_KEY) {
      return;
    }

    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: APP_INSIGHTS_INSTRUMENTATION_KEY,
      },
    });

    this.appInsights.loadAppInsights();
  };

  public trackError = (error: Error, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(error, properties, SeverityLevel.Error);
  };

  public trackCriticalError = (error: Error, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(error, properties, SeverityLevel.Critical);
  };

  public trackInfo = (message: string, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(message, properties, SeverityLevel.Information);
  };

  public trackWarning = (message: string, properties: IAppInsightsCustomProperties): void => {
    this.trackValue(message, properties, SeverityLevel.Warning);
  };
}

export default new Logger();
